'use strict';

// external

// shared imports
import Joi from '~/shared/src/utilities/joiExtended';
import Item, {ItemAttributes} from '~/shared/src/ci/Item';
import {ReferenceAttributes} from '~/shared/src/ci/Reference';
import {
    buildDataDescriptor,
    DataDescriptor,
    DataStructure,
    StaticDataDescriptor
} from '~/shared/src/utilities/dataStructure';
import {BaseRequestParameters} from '~/shared/src/model/BaseRequest';
import {
    Chart,
    ChartType,
    LegendAlign,
    LegendPosition,
    TimeSeriesIntervalUnit
} from '~/shared/src/utilities/chart';
import {ChartDataSet} from '~/shared/src/utilities/chartDataSet';
import {ChartScale} from '~/shared/src/utilities/chartScale';
import {Message} from '~/shared/src/utilities/message';
import {Query} from '~/shared/src/utilities/query';
import {
    LoadMetaDataRowData,
    LoadMetaDataRowDataMapping
} from '~/shared/src/utilities/modules/overview';
import {
    ColumnFilter,
    ColumnSorter,
    filtersSchema,
    sortersSchema
} from '~/shared/src/validationSchemes/modules/overview';
import {dataExtraction} from '~/shared/src/utilities/dataExtraction';
import {getDataStructureForSqlQuery} from '~/shared/src/utilities/modules/queryLoader';
import {Condition} from '~/shared/src/utilities/condition';


/*** method types ****************************************************************************************************/

/*** loadConfig ***/
export type LoadConfigParameters = Partial<BaseRequestParameters> & {
    viewKey : string;
};
export type LoadConfigResult = {
    success : boolean;
    data : {
        schema : Schema;
        viewName : string;
        title : string | Message;
        tabTitle? : string | Message;
        tooltipTitle? : string | Message;
        iconCls : string;
        type : ChartType;
        legendAlign : LegendAlign;
        legendPosition : LegendPosition;
        timeSeriesIntervalUnit : false | TimeSeriesIntervalUnit;
        dataStructure : DataStructure;
        xAxisLabel? : string | Message;
        xAxisLabelDataDescriptor : DataDescriptor;
        dataDescriptors : DataDescriptor[];
        dataSets : ChartDataSet[];
        scales : ChartScale[];
        configHash : string;
        queryType : Query[ 'type' ];
    };
    metaData : {
        metrics : Metric[];

        // debug
        cypher? : string;
        sql? : string;
        parameters? : { [ parameter : string ] : any };
    };
};

export type LoadConfigDataParameters = Partial<BaseRequestParameters> & ( {
    viewId : string;
    viewKey : string;
} );
export type LoadConfigDataResult = {
    success : boolean;
    data : {
        items : ItemAttributes[];
        references : ReferenceAttributes[];
    };
    metaData : {
        metrics : Metric[];

        // debug
        cypher? : string;
        sql? : string;
        parameters? : {
            [ parameter : string ] : any
        };
    };
};

export type  LoadColumnDataDescriptorsParameters = {
    schema : Schema;
    type : Exclude<Query['type'], 'DataStructure'>;
    query : string;
    parameters? : string[];
};
export type LoadColumnDataDescriptorsResult = {
    success : boolean;
    data : {
        columnDataDescriptors : StaticDataDescriptor[];
    };
    metaData : {
        metrics : Metric[];
    };
};

/*** clearConfig ***/
export type ClearConfigParameters = {
    configHash? : string;
};
export type ClearConfigResult = {
    success : boolean;
    metaData : {
        metrics : Metric[];
    };
};


export type GetChartItemIdByDisplayAttributeParameters = {
    displayAttributeValue : string;
};
export type GetChartItemIdByDisplayAttributeResult = {
    success : true;
    data : {
        rootItemId? : number;
    };
    metaData : {
        metrics : Metric[];

        // debug
        getChartItemId? : {
            sql? : string;
            parameters? : {
                [ parameter : string ] : any
            };
        };
    };
};


export type GetViewKeyByNameParameters = {
    viewName : string;
};
export type GetViewKeyByNameResult = {
    success : true;
    data : {
        viewKey? : string;
    };
    metaData : {
        metrics : Metric[];

        // debug
        sql? : string;
        parameters? : { [ parameter : string ] : any };
    };
};

/*** loadData common***/
export type LoadDataCommonParameters = {
    filters? : ColumnFilter[];
    sorters? : ColumnSorter[];
    parameters? : {
        [ parameter : string ] : string;
    };
};
export const loadDataCommonParameterDefaults = {
    filters: [],
    sorters: []
};

export const loadDataCommonParametersSchema = Joi.object( {
    filters   : filtersSchema,
    sorters   : sortersSchema,
    parameters: Joi.object()
                   .pattern( /.*/, Joi.alternatives().try( Joi.string().allow( '' ), Joi.number(), Joi.boolean() ) )
                   .description( 'The parameters for the query with their values.' )
} );

/*** loadData ***/
export type LoadDataParameters = Partial<BaseRequestParameters> & LoadDataCommonParameters & {
    viewKey : string;
    configHash? : string;
};
export type LoadDataResult = {
    success : boolean;
    data : {};
    metaData : {
        rowData : LoadMetaDataRowData[];
        rowDataMapping : LoadMetaDataRowDataMapping;
        total : number;
        metrics : Metric[];

        // debug
        cypher? : string;
        sql? : string;
        parameters? : { [ parameter : string ] : any };
    };
};

/*** loadDataForDataStructure ***/
export type LoadDataForDataStructureParameters = Partial<BaseRequestParameters> & LoadDataCommonParameters & {
    configData : Chart[ 'data' ];
    dataDescriptorsToSelect : DataDescriptor[];
    dataStructure : DataStructure;
    xAxisLabelDataDescriptor? : DataDescriptor;
};
/*** loadDataForQuery ***/
export type LoadDataForQueryParameters = Partial<BaseRequestParameters> & LoadDataCommonParameters & {
    configData : Chart[ 'data' ];
    dataDescriptorsToSelect : DataDescriptor[];
    query : string;
    type : Exclude<Query[ 'type' ], 'DataStructure'>;
};

/*** deleteChart ***/
export type DeleteChartParameters = {
    viewId : number;
    historizeOnly? : boolean;
};
export type DeleteChartResult = {
    success : boolean;
    metaData : {
        metrics : Metric[];
    }
};


export const getXAxisLabelDescriptor = ( item : Item, columnDataDescriptors : DataDescriptor[], createDataDescriptor : boolean = false ) : DataDescriptor | undefined => {
    const data = {
        ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
        ...dataExtraction.extractPropertiesOfItem( item )
    };

    let dataDescriptor = columnDataDescriptors.find( cDD => cDD.field === data.xAxisLabelQueryFieldName );

    if ( createDataDescriptor && !dataDescriptor ) {
        dataDescriptor = buildDataDescriptor( { queryFieldName: data.xAxisLabelQueryFieldName ?? 'createdQueryFieldName' }, getDataStructureForSqlQuery() );

        if ( dataDescriptor ) {
            dataDescriptor.fullAlias = `createdDataDescriptor::${dataDescriptor.fullAlias}`;
        }
    }

    return dataDescriptor;
}

export const getDataSets = ( columnDataDescriptors : DataDescriptor[], item : Item, dataContext : string, createDataDescriptor : boolean = false ) : ChartDataSet[] => {
    const retVal : ChartDataSet[] = [],
          dataStructure           = getDataStructureForSqlQuery(),
          dataSets                = dataExtraction.extractChartDataSets( item, dataContext, dataStructure );

    for ( const dataSet of dataSets ) {
        processDataSet( dataSet, columnDataDescriptors, createDataDescriptor );

        if ( dataSet.dataDescriptor ) {
            retVal.push( dataSet );
        }
    }

    return retVal;
}

export const processDataSet = ( dataSet : ChartDataSet, columnDataDescriptors : DataDescriptor[], createDataDescriptor : boolean = false ) => {
    const dataStructure = getDataStructureForSqlQuery();

    let dataDescriptor = columnDataDescriptors.find( cDD => cDD.alias === dataSet.data.queryFieldName );

    if ( !dataDescriptor && createDataDescriptor ) {
        dataDescriptor = buildDataDescriptor( { queryFieldName: dataSet.data.queryFieldName ?? 'createdQueryFieldName' }, dataStructure );

        if ( dataDescriptor ) {
            dataDescriptor.fullAlias = `createdDataDescriptor::${dataDescriptor.fullAlias}`;
        }
    }

    if ( dataDescriptor ) {
        dataSet.dataDescriptor = dataDescriptor;

        if ( !dataSet.dataDescriptors.map( dD => dD.alias ).includes( dataDescriptor.alias ) ) {
            dataSet.dataDescriptors.push( dataDescriptor );
        }

        // get dataDescriptors for messages
        if ( dataSet.messages && Object.keys( dataSet.messages ).length ) {
            for ( const message of Object.values( dataSet.messages ) ) {

                // get dataDescriptor(s) for string message via columnDataDescriptors
                if ( typeof message === 'string' ) {
                    const attributePaths = __( message ).match( /((?:[\w\p{L}\p{N}:]+\.)+[\w\p{L}\p{N}:]+)/gimu ) ?? [ __( message ) ];

                    for ( let attributePath of attributePaths ) {
                        attributePath = attributePath.replaceAll( /(^\{|\}$)/g, '' );

                        const messageDataDescriptor = columnDataDescriptors?.find( cDD => cDD.field === attributePath );

                        if ( messageDataDescriptor && !dataSet.dataDescriptors.map( dD => dD.alias ).includes( messageDataDescriptor.alias ) ) {
                            dataSet.dataDescriptors.push( messageDataDescriptor );
                        }
                    }
                }
            }
        }

        // get dataDescriptors of style conditions
        if ( dataSet.styles && Object.keys( dataSet.styles ).length ) {
            const getConditionDataDescriptorRecursive = ( condition : Condition ) => {

                // get dataDescriptor for string leftExpression via columnDataDescriptors
                if ( typeof condition.leftExpression === 'string' ) {
                    const attributePath       = condition.leftExpression,
                          styleDataDescriptor = columnDataDescriptors?.find( cDD => cDD.field === attributePath );

                    if ( styleDataDescriptor && !dataSet.dataDescriptors.find( dD => dD.alias === styleDataDescriptor.alias ) ) {
                        dataSet.dataDescriptors.push( styleDataDescriptor );
                    }
                }

                if ( Array.isArray( condition.leftExpression ) ) {
                    for ( const subCondition of condition.leftExpression ) {
                        getConditionDataDescriptorRecursive( subCondition );
                    }
                }

                // get dataDescriptor for string rightExpression via columnDataDescriptors
                if ( typeof condition.rightExpression === 'string' ) {
                    const attributePath       = condition.rightExpression,
                          styleDataDescriptor = columnDataDescriptors?.find( cDD => cDD.field === attributePath );

                    if ( styleDataDescriptor && !dataSet.dataDescriptors.find( dD => dD.alias === styleDataDescriptor.alias ) ) {
                        dataSet.dataDescriptors.push( styleDataDescriptor );
                    }
                }

                if ( Array.isArray( condition.rightExpression ) ) {
                    for ( const subCondition of condition.rightExpression ) {
                        getConditionDataDescriptorRecursive( subCondition );
                    }
                }
            };

            for ( const style of Object.keys( dataSet.styles ) ) {
                for ( const subStyle of dataSet.styles[ style ] ?? [] ) {
                    if ( subStyle.condition ) {
                        getConditionDataDescriptorRecursive( subStyle.condition );
                    }
                }
            }
        }
    }
}
